<template>
  <div class="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
    <!-- Header Section -->
    <div class="md:grid md:grid-cols-3 md:gap-6">
      <div class="md:col-span-1">
        <div class="px-4 sm:px-0">
          <h3 class="text-lg font-medium leading-6 text-gray-900">Create Food Batch</h3>
          <p class="mt-1 text-sm text-gray-600">
            Fill out the form below to create a new food batch.
          </p>
        </div>
      </div>

      <!-- Form Section -->
      <div class="mt-5 md:mt-0 md:col-span-2">
        <form :action="company.buildCompanyRoute('food-batches')" method="POST">
          <input type="hidden" name="_token" :value="csrf" />
          <div class="shadow overflow-hidden sm:rounded-md">
            <div class="px-4 py-5 bg-white space-y-6 sm:p-6">
              <!-- Food Selection -->
              <div>
                <label for="food" class="block text-sm font-medium text-gray-700">Food</label>
                <select2
                  :options="formattedFoods"
                  v-model="form.food"
                  name="food"
                  :multiple="false"
                  :close-on-select="true"
                  placeholder="Select a food"
                  @input="onFoodChange"
                ></select2>
                <span v-if="errors.food" class="text-red-500 text-sm">{{ errors.food[0] }}</span>
              </div>

              <!-- Action Selection -->
              <div>
                <label for="action" class="block text-sm font-medium text-gray-700">Action</label>
                <select2
                  :options="actions"
                  v-model="form.action"
                  name="action"
                  :multiple="false"
                  :close-on-select="true"
                  placeholder="Select an action"
                ></select2>
                <span v-if="errors.action" class="text-red-500 text-sm">{{ errors.action[0] }}</span>
              </div>

              <!-- Lot Number -->
              <div>
                <label for="lot" class="block text-sm font-medium text-gray-700">Lot #</label>
                <input
                  type="text"
                  name="lot"
                  id="lot"
                  v-model="form.lot"
                  class="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  placeholder="Enter Lot Number"
                />
                <span v-if="errors.lot" class="text-red-500 text-sm">{{ errors.lot[0] }}</span>
              </div>

              <!-- Zone Selection -->
              <div>
                <label for="zone_id" class="block text-sm font-medium text-gray-700">Zone</label>
                <select2-group :options='areaZones'
                               :close-on-select="true"
                               :allow-clear="false"
                               :multiple="false"
                               v-model='form.zone'
                               id="zone_id"
                               name="zone_id"
                               placeholder="Select a Zone for this point"
                               class="p-2 m-2"
                ></select2-group>
                <span v-if="errors.zone" class="text-red-500 text-sm">{{ errors.zone[0] }}</span>
              </div>

              <!-- Limiting Temperature -->
              <div v-if="form.action == 'cooking' || form.action == 'hot_holding' || form.action == 'serving'">
                <label for="limiting_value" class="block text-sm font-medium text-gray-700">Limiting Temperature (&deg;F)</label>
                <input
                  type="number"
                  name="limiting_value"
                  id="limiting_value"
                  v-model.number="form.limiting_value"
                  class="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  :min="0"
                  step="0.1"
                />
                <span v-if="errors.limiting_value" class="text-red-500 text-sm">{{ errors.limiting_value[0] }}</span>
              </div>
            </div>

            <!-- Submit Button -->
            <div class="px-4 py-3 bg-gray-50 text-right sm:px-6">
              <button
                type="submit"
                class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Create Food Batch
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, watch, computed } from 'vue';
import Select2 from "../elements/select2.vue";
import Select2Group from "../elements/select2-group.vue";
import {SonicuCoreMixin} from "../../mixins/SonicuCoreMixin";

export default {
  name: 'FoodBatchCreate',
  components: {Select2Group, Select2},
  props: {
    foods: {
      type: Object,
      required: true,
    },
    actions: {
      type: Object,
      required: true,
    },
    areaZones: {
      type: Object,
      required: true,
    },
  },
  mixins: [SonicuCoreMixin],
  setup(props) {
    // Reactive form data
    const form = reactive({
      food: null,
      action: null,
      lot: '',
      zone: null,
      limiting_value: null,
    });

    // Limiting temperatures based on selected food
    const limitingTemperatures = ref({});

    // Initialize limitingTemperatures from props.foods
    onMounted(() => {
      props.foods.forEach(food => {
        limitingTemperatures.value[food.id] = food.default_limiting_value || null;
      });
    });

    const formattedFoods = computed(() => {
      return props.foods.reduce((acc, food) => {
        acc[food.id] = food.name;
        return acc;
      }, {});
    });

    // Validation errors
    const errors = reactive({});

    // Handle changes in the Food selection
    const onFoodChange = selectedFoodId => {
      if (selectedFoodId && limitingTemperatures.value[selectedFoodId] !== undefined) {
        form.limiting_value = limitingTemperatures.value[selectedFoodId];
      } else {
        form.limiting_value = null;
      }
    };

    return {
      form,
      errors,
      onFoodChange,
      formattedFoods,
    };
  },
};
</script>

<style scoped>
/* Optional: Add any component-specific styles here */
</style>
